import { Container } from 'inversify'
import { Types } from './types'
import { ShellPresenter } from '@core/infra/shell/shell.presenter'
import { ThemePresenter } from '@core/infra/theme/theme.presenter'
import { FeedbackRepository } from '../feedback/feedback.repository'
import { FeedbackConfig } from '../feedback/feedback.config'
import { FeedbackPresenter } from '../feedback/feedback.presenter'
import { Router } from '../routing/router'
import { RouterRepository } from '../routing/router.repository'
import { NavigationRepository } from '@core/infra/navigation/navigation.repository'
import { FiltersConfig } from '@core/infra/filters/filters.config'
import { FiltersRepository } from '@core/infra/filters/filters.repository'
import { FilterUrlService } from '@core/infra/filters/filter-url.service'
import { FreshnessRepository } from '@core/infra/freshness/freshness.repository'
import { OfflineCacheRepository } from '@core/infra/offline/offline-cache.repository'
import { OfflineShellRepository } from '@core/infra/offline/offline-shell.repository'
import { SuggestionsRepository } from '@core/infra/suggestions/suggestions.repository'
import { ProfileRepository } from '@core/infra/authentication/profile.repository'
import { PushRepository } from '@core/infra/push/push.repository'
import { ViewPreferencesRepository } from '@core/infra/preferences/view-preferences.repository'
import { DraftRepository } from '@core/infra/drafts/draft.repository'
import { SelectionRepository } from '@core/infra/selection/selection.repository'
import { DraftGuardService } from '@core/infra/drafts/draft-guard.service'
import { BooksRepository } from '@features/books/books.repository'
import { GamesListRepository } from '@features/games/games-list.repository'
import { LobbyRepository } from '@features/lobby/lobby.repository'
import { GameRepository } from '@features/game/game.repository'
import { ActiveGamesRepository } from '@features/game/active-games.repository'
import { UserModel } from '@core/infra/authentication/user.model'
import { AuthenticationRepository } from '@core/infra/authentication/authentication.repository'
import { AdminModeRepository } from '@core/infra/authentication/admin-mode.repository'
import { AdminModeTogglePresenter } from '@core/infra/authentication/admin-mode-toggle.presenter'
import { ShellRepository } from '@core/infra/shell/shell.repository'
import { ThemeRepository } from '@core/infra/theme/theme.repository'
import { ViewportConfig } from '@core/infra/viewport/viewport.config'
import { NarrowViewportRepository } from '@core/infra/viewport/narrow-viewport.repository'
import { InfraDemoRepository } from '@features/infra-demo/infra-demo.repository'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { TranslationsConfig } from '@core/infra/i18n/i18n.config'
import { ThemeConfig } from '@core/infra/theme/theme.config'
import { NotificationRepository } from '@core/infra/notifications/notification.repository'
import { DialogRepository } from '@core/infra/dialog/dialog.repository'
import { CommandPaletteRepository } from '@core/infra/command-palette/command-palette.repository'
import { NavigationCommandSource } from '@core/infra/command-palette/navigation.command-source'
import { SearchRepository } from '@core/infra/search/search.repository'
import { ConnectivityRepository } from '@core/infra/connectivity/connectivity.repository'
import { VersionRepository } from '@core/infra/connectivity/version.repository'
import { PresenceRepository } from '@core/infra/presence/presence.repository'
import { InboxRepository } from '@core/infra/inbox/inbox.repository'
import { AdminErrorLookupRepository } from '@core/infra/observability/errors/admin-error-lookup.repository'
import { AdminUsersRepository } from '@core/infra/authentication/admin/admin-users.repository'
import { AdminAuditRepository } from '@core/infra/observability/audit/admin-audit.repository'
import { PageTitleConfig } from '@core/infra/page-title/page-title.config'
import { PageTitlePresenter } from '@core/infra/page-title/page-title.presenter'
import { ExitGuardPresenter } from '@core/infra/exit-guard/exit-guard.presenter'
import { LeaveConfirmationService } from '@core/infra/exit-guard/leave-confirmation.service'
import { OperationRepository } from '@core/operations/operation.repository'

export class BaseIOC {
  container

  constructor() {
    this.container = new Container({
      autobind: true,
      defaultScope: 'Transient',
    })
  }

  buildBaseTemplate = () => {
    // Config classes: declarative data, separated from the Repositories that
    // hold the live state — the RoutesConfig / NavigationConfig shape. Singleton
    // because they are immutable tables; injectable so a test or a future
    // variant can bind a different one.
    this.container.bind(FeedbackConfig).to(FeedbackConfig).inSingletonScope()
    this.container.bind(TranslationsConfig).to(TranslationsConfig).inSingletonScope()
    this.container.bind(ThemeConfig).to(ThemeConfig).inSingletonScope()
    this.container.bind(ViewportConfig).to(ViewportConfig).inSingletonScope()
    this.container.bind(PageTitleConfig).to(PageTitleConfig).inSingletonScope()

    this.container.bind(FeedbackRepository).to(FeedbackRepository).inSingletonScope()
    this.container.bind(FeedbackPresenter).toSelf().inTransientScope()

    // Async-operation coordination (spec/50-mechanisms.md). Bound by class,
    // never a Types symbol — Types is reserved for interfaces/multi-bind
    // identifiers. Bound before the Repositories below since they inject it
    // through their own constructors to call createOwner() during
    // construction.
    this.container.bind(OperationRepository).to(OperationRepository).inSingletonScope()

    // Transient by default would give every @inject(Router) site its own instance —
    // harmless while it only held a throwaway isLoading flag, but redirectTarget
    // (post-login destination) must be shared across all of them.
    this.container.bind(Router).to(Router).inSingletonScope()
    this.container.bind(RouterRepository).to(RouterRepository).inSingletonScope()
    this.container.bind(NavigationRepository).to(NavigationRepository).inSingletonScope()
    // Declarative table of what each list offers, like NavigationConfig.
    this.container.bind(FiltersConfig).to(FiltersConfig).inSingletonScope()
    // Holds which filters are active per list. Singleton because the toolbar
    // that sets a filter unmounts when the user leaves the page and the filter
    // is expected to survive that (P-28) — which also makes it user-scoped, so
    // it is registered below.
    this.container.bind(FiltersRepository).to(FiltersRepository).inSingletonScope()
    // Keeps the URL and the narrowing agreeing, for every list at once.
    this.container.bind(FilterUrlService).to(FilterUrlService).inSingletonScope()
    // When each loadIfNeeded() scope last completed a load, so a route
    // re-entry can bound how long it reuses in-memory data instead of
    // showing it forever. Singleton and user-scoped for the same reason
    // FiltersRepository is (registered below).
    this.container.bind(FreshnessRepository).to(FreshnessRepository).inSingletonScope()
    // Last-seen list data in localStorage, served to a load() only when the
    // device is offline. Singleton and user-scoped (registered below); also an
    // AppLifecycle participant, for the boot-time prune of foreign/expired entries.
    this.container.bind(OfflineCacheRepository).to(OfflineCacheRepository).inSingletonScope()
    // Registers the service worker at boot for every user, so the app shell
    // loads offline. AppLifecycle only — it holds no user-scoped state and
    // never prompts for notification permission.
    this.container.bind(OfflineShellRepository).to(OfflineShellRepository).inSingletonScope()
    // The principals this viewer may be offered by name/team, keyed by scope
    // (core/infra/principals/principal-labels.ts is the first registered
    // scope). Singleton because several forms ask the same question of the
    // same endpoint and want the same copy; user-scoped (registered below)
    // because the answer is this viewer's and the next login must not
    // inherit it.
    this.container.bind(SuggestionsRepository).to(SuggestionsRepository).inSingletonScope()
    // How this viewer wants lists laid out. Singleton because it is one habit
    // across every table rather than a per-page setting, and user-scoped
    // (registered below) for the same reason the filters are.
    this.container.bind(ViewPreferencesRepository).to(ViewPreferencesRepository).inSingletonScope()
    // Every form's unsaved text, in one place. Singleton because it must outlive
    // the surface that renders it — that is the whole point (see the class
    // comment); user-scoped and IUnsavedWork, both registered below.
    this.container.bind(DraftRepository).to(DraftRepository).inSingletonScope()
    // Which rows are ticked, per list. Singleton and user-scoped for the same
    // reasons the filters are; deliberately never written to the URL.
    this.container.bind(SelectionRepository).to(SelectionRepository).inSingletonScope()
    this.container.bind(DraftGuardService).to(DraftGuardService).inSingletonScope()
    this.container.bind(BooksRepository).to(BooksRepository).inSingletonScope()
    this.container.bind(GamesListRepository).to(GamesListRepository).inSingletonScope()
    // Singleton: the current game must be visible to every screen the lobby
    // route family renders, not just the one Presenter that happened to load it.
    this.container.bind(LobbyRepository).to(LobbyRepository).inSingletonScope()
    // Singleton: the live board, both seats' setup progress and the 3s
    // opponent poll must be visible to the page and the board section alike,
    // and must survive the board component remounting mid-poll.
    this.container.bind(GameRepository).to(GameRepository).inSingletonScope()
    // Singleton: the signed-in user's own profile, shared by whichever screen
    // reads or edits it and anything else that wants the current display name.
    this.container.bind(ProfileRepository).to(ProfileRepository).inSingletonScope()
    this.container.bind(PushRepository).to(PushRepository).inSingletonScope()
    this.container.bind(UserModel).to(UserModel).inSingletonScope()

    this.container.bind(AuthenticationRepository).to(AuthenticationRepository).inSingletonScope()

    // Everything holding state that dies with the session. Logout resets all of
    // these without AuthenticationRepository naming any of them — adding a
    // user-scoped repository means adding one line here, not editing auth.
    // toService aliases the singleton above rather than constructing a second.
    this.container.bind(Types.IUserScopedState).toService(FiltersRepository)
    this.container.bind(Types.IUserScopedState).toService(FreshnessRepository)
    this.container.bind(Types.IUserScopedState).toService(OfflineCacheRepository)
    this.container.bind(Types.IUserScopedState).toService(SuggestionsRepository)
    this.container.bind(Types.IUserScopedState).toService(PresenceRepository)
    this.container.bind(Types.IUserScopedState).toService(InboxRepository)
    this.container.bind(Types.IUserScopedState).toService(ViewPreferencesRepository)
    this.container.bind(Types.IUserScopedState).toService(DraftRepository)
    this.container.bind(Types.IUserScopedState).toService(SelectionRepository)
    this.container.bind(Types.IUserScopedState).toService(BooksRepository)
    this.container.bind(Types.IUserScopedState).toService(GamesListRepository)
    this.container.bind(Types.IUserScopedState).toService(LobbyRepository)
    this.container.bind(Types.IUserScopedState).toService(GameRepository)
    this.container.bind(Types.IUserScopedState).toService(ActiveGamesRepository)
    this.container.bind(Types.IUserScopedState).toService(ProfileRepository)
    this.container.bind(Types.IUserScopedState).toService(PushRepository)
    this.container.bind(Types.IUserScopedState).toService(RouterRepository)
    this.container.bind(Types.IUserScopedState).toService(NavigationRepository)
    this.container.bind(Types.IUserScopedState).toService(AdminUsersRepository)
    this.container.bind(Types.IUserScopedState).toService(AdminErrorLookupRepository)
    this.container.bind(Types.IUserScopedState).toService(AdminAuditRepository)
    this.container.bind(Types.IUserScopedState).toService(AdminModeRepository)
    // DialogComponent mounts on the bare shell too (so it can render on
    // /login), and its Promise is settled by a click that may never come if
    // the session ends first — without this a stale confirm() (e.g. the
    // leave-confirm Router.goToId awaits) resolves into the next session.
    this.container.bind(Types.IUserScopedState).toService(DialogRepository)
    // Singleton so the palette's open/query state survives the trigger and
    // the modal being different component instances (see the class comment)
    // — the same lifetime reasoning makes it leak into the next session
    // without this.
    this.container.bind(Types.IUserScopedState).toService(CommandPaletteRepository)
    this.container.bind(Types.IUserScopedState).toService(SearchRepository)
    // Its edited-but-not-saved demo note must not leak to the next user the
    // same way any real form's draft would not — its persisted draftNotePm is
    // untouched by this, see the class comment.
    this.container.bind(Types.IUserScopedState).toService(InfraDemoRepository)
    // Deliberately NOT here: FeedbackRepository, because every scope is
    // configured clearOnRouteChange and every logout path navigates, so it is
    // already empty by the time the next user sees anything; and
    // NotificationRepository, because the "your session expired" toast is
    // raised *after* logOut() precisely so it survives the redirect to /login.
    // Registering either would be teardown with nothing to tear down, or a trap
    // for the one message that has to outlive the session.

    // Shared Presenters, not screen-specific ones: each owns a startup step and
    // (for theme) a live OS-preference subscription, which is state of a kind
    // even though neither holds observable data. §7.2 scopes those Singleton.
    this.container.bind(ShellPresenter).to(ShellPresenter).inSingletonScope()
    this.container.bind(ThemePresenter).to(ThemePresenter).inSingletonScope()
    // Owns a live reaction on the committed route, which is state of a kind.
    this.container.bind(PageTitlePresenter).to(PageTitlePresenter).inSingletonScope()
    // Owns the beforeunload subscription.
    this.container.bind(ExitGuardPresenter).to(ExitGuardPresenter).inSingletonScope()
    // Same reasoning as ThemePresenter: drives a startup step (restore()) and
    // must be the one instance TopBarComponent's useInjection resolves, or a
    // Transient copy's restore() would never have run.
    this.container.bind(AdminModeTogglePresenter).to(AdminModeTogglePresenter).inSingletonScope()

    // What the Router asks before an app-initiated navigation. Bound to the
    // interface so routing keeps no dependency on dialogs or translations.
    this.container.bind(Types.ILeaveConfirmation).to(LeaveConfirmationService).inSingletonScope()

    // One-time startup steps, driven by AppPresenter. Order-independent by
    // contract — anything with a real ordering constraint stays written out in
    // AppPresenter.load(). See core/interfaces/app-lifecycle.ts.
    this.container.bind(Types.IAppLifecycle).toService(ThemePresenter)
    this.container.bind(Types.IAppLifecycle).toService(ShellPresenter)
    this.container.bind(Types.IAppLifecycle).toService(ConnectivityRepository)
    this.container.bind(Types.IAppLifecycle).toService(OfflineCacheRepository)
    this.container.bind(Types.IAppLifecycle).toService(OfflineShellRepository)
    this.container.bind(Types.IAppLifecycle).toService(NarrowViewportRepository)
    this.container.bind(Types.IAppLifecycle).toService(I18nRepository)
    this.container.bind(Types.IAppLifecycle).toService(VersionRepository)
    // Starts its own poll (see InboxRepository.initialize) — same reasoning
    // as PresenceRepository, immediately below.
    this.container.bind(Types.IAppLifecycle).toService(InboxRepository)
    // Starts its own poll (see PresenceRepository.initialize) — same
    // AppLifecycle participant shape as VersionRepository just above.
    this.container.bind(Types.IAppLifecycle).toService(PresenceRepository)
    // Starts its own poll (see ActiveGamesRepository's own doc comment) —
    // same shape again, feeding the sidebar's "Play" section app-wide.
    this.container.bind(Types.IAppLifecycle).toService(ActiveGamesRepository)
    // The one implementation of `core/interfaces/active-games-source.ts` —
    // bound here, not in `core/`, since only a composition root may name a
    // domain feature (CI-3).
    this.container.bind(Types.IActiveGamesSource).toService(ActiveGamesRepository)
    this.container.bind(Types.IAppLifecycle).toService(PageTitlePresenter)
    this.container.bind(Types.IAppLifecycle).toService(ExitGuardPresenter)
    this.container.bind(Types.IAppLifecycle).toService(FilterUrlService)
    this.container.bind(Types.IAppLifecycle).toService(AdminModeTogglePresenter)

    // Preference state. Singletons for the same reason every other repository
    // is one: they hold state the whole app reads, and a transient binding
    // would hand each consumer its own copy that nobody else's writes reach.
    this.container.bind(ShellRepository).to(ShellRepository).inSingletonScope()
    this.container.bind(ThemeRepository).to(ThemeRepository).inSingletonScope()
    // Holds a session-wide viewport-width signal one media-query subscription
    // updates for every consumer at once (P-14), same reasoning as ShellRepository.
    this.container.bind(NarrowViewportRepository).to(NarrowViewportRepository).inSingletonScope()
    this.container.bind(AdminModeRepository).to(AdminModeRepository).inSingletonScope()
    this.container.bind(I18nRepository).to(I18nRepository).inSingletonScope()
    this.container.bind(NotificationRepository).to(NotificationRepository).inSingletonScope()
    this.container.bind(DialogRepository).to(DialogRepository).inSingletonScope()
    this.container.bind(CommandPaletteRepository).to(CommandPaletteRepository).inSingletonScope()
    // The palette's two CommandSource participants (core/interfaces/command-
    // source.ts) — the static nav tree and backend search. Singleton for the
    // same reason every other Repository-shaped state is: one instance
    // shared by whatever resolves it, not a copy per consumer.
    this.container.bind(NavigationCommandSource).to(NavigationCommandSource).inSingletonScope()
    this.container.bind(SearchRepository).to(SearchRepository).inSingletonScope()
    this.container.bind(ConnectivityRepository).to(ConnectivityRepository).inSingletonScope()
    this.container.bind(VersionRepository).to(VersionRepository).inSingletonScope()
    // Singleton: holds session-wide online-status state one poll must update
    // for every consumer at once (P-14), same reasoning as VersionRepository.
    this.container.bind(PresenceRepository).to(PresenceRepository).inSingletonScope()
    // Singleton: one poll of the caller's own open games, shared by the
    // sidebar and any other consumer (P-14), same reasoning as PresenceRepository.
    this.container.bind(ActiveGamesRepository).to(ActiveGamesRepository).inSingletonScope()
    // Singleton for the same reason: one poll, one toast per new item, shared
    // by the bell and any other consumer, not a copy each would poll separately.
    this.container.bind(InboxRepository).to(InboxRepository).inSingletonScope()
    this.container.bind(InfraDemoRepository).to(InfraDemoRepository).inSingletonScope()
    this.container.bind(AdminErrorLookupRepository).to(AdminErrorLookupRepository).inSingletonScope()
    this.container.bind(AdminUsersRepository).to(AdminUsersRepository).inSingletonScope()
    this.container.bind(AdminAuditRepository).to(AdminAuditRepository).inSingletonScope()

    // Anything holding work the user could lose registers here. It is asked
    // only when the document is about to go away — nothing is prevented (§R).
    this.container.bind(Types.IUnsavedWork).toService(InfraDemoRepository)
    // The one participant that speaks for every real form in the app.
    this.container.bind(Types.IUnsavedWork).toService(DraftRepository)

    // The command palette's result sources (core/interfaces/command-source.ts)
    // — adding a third means one line here, not an edit to CommandPaletteRepository.
    this.container.bind(Types.ICommandSource).toService(NavigationCommandSource)
    this.container.bind(Types.ICommandSource).toService(SearchRepository)

    // These Presenters used to be Singletons, each with a comment explaining
    // which shared state made that necessary: the notification stack, the one
    // pending dialog, the online flag, the latest version, the sidebar mode,
    // the theme. All of it now lives in the Repositories bound above, so none
    // of these holds state of its own — they read a Repository and expose a
    // ViewModel. That makes them screen-specific Presenters, which §7.2 scopes
    // Transient, which is the container default: no bind needed at all
    // (autobind resolves them).
    //
    // useInjection() resolves once per component instance (useState initialiser),
    // so Transient means one Presenter per mounted component, not one per render.
    return this.container
  }
}
